// 聊天滚动管理 Hook

import { useState, useCallback, useEffect, useRef } from 'react';

// 滚动管理 Hook
export function useChatScroll(
  messages: any[], // 使用 any[] 避免循环依赖
  hasMoreHistory: boolean,
  isLoadingHistory: boolean,
  loadMoreHistory: () => void,
  shouldAutoScroll: boolean = true // 新增参数控制是否自动滚动
) {
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // 智能滚动 - 只有在用户位于底部时才自动滚动
  useEffect(() => {
    if (isUserAtBottom && shouldAutoScroll) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom, isUserAtBottom, shouldAutoScroll]);

  // 滚动检测，实现懒加载和底部检测 - 添加节流优化
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      // 节流处理，避免频繁触发
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainer;

        // 检测是否在顶部（懒加载历史消息）
        if (scrollTop === 0 && hasMoreHistory && !isLoadingHistory) {
          loadMoreHistory();
        }

        // 检测是否在底部（阈值为50px，避免精确匹配问题）
        const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
        setIsUserAtBottom(isAtBottom);
      }, 16); // 约60fps的节流
    };

    messagesContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
        scrollTimeout = null;
      }
      messagesContainer.removeEventListener('scroll', handleScroll);
    };
  }, [hasMoreHistory, isLoadingHistory, loadMoreHistory]);

  // 强制滚动到底部（用于用户发送消息时）
  const forceScrollToBottom = useCallback(() => {
    setIsUserAtBottom(true);
  }, []);

  return {
    isUserAtBottom,
    messagesEndRef,
    messagesContainerRef,
    scrollToBottom,
    forceScrollToBottom
  };
}